import { useState } from 'react';
import type { Bulle } from '../partage/api/types';
import { delaiEnMots, instantPourLeChamp } from '../partage/datesEnMots';
import { FeuilleDesDelais } from './FeuilleDesDelais';
import { valeursDuQuand } from './ficheEnMots';
import { ChampTape, LigneQuiSOuvre } from './Saisies';
import { BORNES_DE_LA_DUREE, bornesDuDelai } from './saisieTapee';
import {
  ECHEANCE,
  LE_MONDE_MET,
  PLUS_UN_DELAI,
  PLUS_UN_RENDEZ_VOUS,
  PLUS_UNE_ECHEANCE,
  POSER_DATE_OU_DELAI,
  RENDEZ_VOUS,
  REPONSE_DU_MONDE,
} from '../partage/vocabulaire';
import './ChampsQuand.css';

/**
 * « Quand » — les trois temps d'une bulle, et ils ne se confondent jamais.
 *
 * C'est la seule raison d'être de ce bloc : **une échéance n'est pas un rendez-vous, et un
 * rendez-vous n'est pas un délai du monde.** L'échéance, le monde l'a posée ; le rendez-vous,
 * c'est toi ; le délai, c'est le temps que met le monde APRÈS ton geste — et lui seul
 * déclenche la relance. Les mélanger dans un seul champ « date » rendrait tous les calculs
 * de la §4 impossibles à écrire, et l'app redeviendrait un carnet.
 */
export function ChampsQuand({
  bulle,
  onChanger,
  auPouce = false,
  ajout: ajoutDemande = null,
  seulement = null,
  toutes = false,
  onFini,
}: {
  bulle: Bulle;
  onChanger: (champs: Partial<Bulle>) => void;
  /**
   * Au téléphone (ticket 08 du chantier telephone), « + Poser une date ou un délai » ouvre en
   * bas de l'écran la petite feuille des délais rapides. Les trois lignes restent au-dessus :
   * elles disent ce qui est posé, et gardent la saisie fine (les mois, le jour où tu as agi).
   */
  auPouce?: boolean;
  /**
   * La ligne qu'on vient d'ajouter par « + Échéance », « + Rendez-vous » ou « + Délai
   * d'attente » (chantier `revue-design`, ticket 02, `design/fiche.html`) : elle paraît ouverte.
   * Les autres ne se montrent que si elles disent quelque chose : une ligne vide ne dit rien,
   * c'est « Ajouter » qui la propose.
   */
  ajout?: TempsAjoute | null;
  /** La ligne ajoutée se range (Entrée, ou on la quitte) : la fiche la retire si rien n'a été posé. */
  onFini?: () => void;
  /**
   * Une seule ligne, celle-là, et ouverte (le panneau Réglages du téléphone, chantier
   * `telephone-projets-fiche`, ticket 04) : on la touche dans les pastilles, ses champs
   * s'ouvrent dessous, les autres lignes ne se montrent pas.
   */
  seulement?: TempsAjoute | null;
  /**
   * Les trois lignes, toujours (l'intercalaire Dates du panneau Réglages de l'ordinateur, chantier
   * reglages-de-la-fiche, modification 02) : une ligne vide dit « + Une échéance » en pointillé,
   * qui ouvre sa saisie sur la ligne.
   */
  toutes?: boolean;
}) {
  const [delais, setDelais] = useState(false);
  const ajout = seulement ?? ajoutDemande;
  const echeance = toutes || (seulement ? seulement === 'echeance' : !!bulle.dateEcheance || ajout === 'echeance');
  const rdv = toutes || (seulement ? seulement === 'rdv' : !!bulle.debut || ajout === 'rdv');
  const delai =
    toutes || (seulement ? seulement === 'delai' : delaiEnMots(bulle) !== null || !!bulle.dateAction || ajout === 'delai');
  if (!echeance && !rdv && !delai && !auPouce) return null;
  // Ce que disent les trois lignes, les mots que la face de la fiche dit aussi (`quandEnMots`).
  const valeurs = valeursDuQuand(bulle);
  return (
    <section className="quand">
      {echeance && (
        <LigneQuiSOuvre
          ouvertAuDepart={ajout === 'echeance'}
          onFini={ajout === 'echeance' ? onFini : undefined}
          nom={ECHEANCE}
          valeur={valeurs.echeance}
          vide="Aucune"
          mention="Le monde l’a posée"
          action="En poser une"
          plus={toutes ? PLUS_UNE_ECHEANCE : undefined}
          tuto={toutes ? REPERES.echeance.tuto : undefined}
        >
          <ChampTape
            sorte="jour"
            className="champs-date"
            data-tuto="champ-date"
            autoFocus
            valeur={bulle.dateEcheance ?? ''}
            onEcrire={(jour) => onChanger({ dateEcheance: jour || null })}
          />
        </LigneQuiSOuvre>
      )}

      {rdv && (
        <LigneQuiSOuvre
          ouvertAuDepart={ajout === 'rdv'}
          onFini={ajout === 'rdv' ? onFini : undefined}
          nom={RENDEZ_VOUS}
          valeur={valeurs.rdv}
          vide="Aucun"
          mention="C’est toi qui l’as posé"
          action="En poser un"
          plus={toutes ? PLUS_UN_RENDEZ_VOUS : undefined}
          tuto={toutes ? REPERES.rdv.tuto : undefined}
        >
          <div className="champs-rdv">
            <ChampTape
              sorte="instant"
              className="champs-date"
              data-tuto="champ-date"
              autoFocus
              valeur={instantPourLeChamp(bulle.debut)}
              onEcrire={(instant) =>
                onChanger({ debut: instant ? new Date(instant).toISOString() : null })
              }
            />
            {bulle.debut && (
              <label className="champs-duree">
                <ChampTape
                  sorte="nombre"
                  className="champs-nombre"
                  bornes={BORNES_DE_LA_DUREE}
                  valeur={bulle.dureeMin != null ? String(bulle.dureeMin) : ''}
                  placeholder="60"
                  onEcrire={(minutes) => onChanger({ dureeMin: minutes ? Number(minutes) : null })}
                />
                min
              </label>
            )}
          </div>
        </LigneQuiSOuvre>
      )}

      {/* Le délai du monde — et lui seul — déclenche la relance. Jamais l'effort. */}
      {delai && (
        <LigneQuiSOuvre
          ouvertAuDepart={ajout === 'delai'}
          onFini={ajout === 'delai' ? onFini : undefined}
          nom={toutes ? REPONSE_DU_MONDE : LE_MONDE_MET}
          valeur={valeurs.delai}
          vide="Rien"
          mention="Après ton geste"
          action="Poser un délai"
          plus={toutes ? PLUS_UN_DELAI : undefined}
          tuto={toutes ? REPERES.delai.tuto : undefined}
        >
          <div className="champs-delai">
            <label>
              J’ai agi le
              <ChampTape
                sorte="jour"
                className="champs-date"
                data-tuto="champ-date"
                valeur={bulle.dateAction ?? ''}
                onEcrire={(jour) => onChanger({ dateAction: jour || null })}
              />
            </label>
            {/*
              Deux unités, et une seule à la fois. Un délai légal s'écrit dans l'unité du
              texte : « deux mois à compter de la réception » ne vaut pas soixante jours, il
              se compte de quantième à quantième. Convertir, c'est mentir d'un à trois jours
              sur une date légale — et c'est ce que la SPEC §9 refuse.
            */}
            <label>
              Ils mettent
              <ChampTape
                sorte="nombre"
                className="champs-nombre"
                data-tuto="champ-delai"
                bornes={bornesDuDelai(enMois(bulle))}
                autoFocus
                valeur={String((enMois(bulle) ? bulle.delaiMois : bulle.delaiJours) ?? '')}
                placeholder="—"
                onEcrire={(nombre) => {
                  // Poser l'un efface l'autre : la règle est dans `commun/` (`autreDelaiEfface`),
                  // le serveur l'écrit et la mise à jour optimiste la montre.
                  const combien = nombre ? Number(nombre) : null;
                  onChanger(enMois(bulle) ? { delaiMois: combien } : { delaiJours: combien });
                }}
              />
            </label>

            <div className="champs-unite" role="group" aria-label="l’unité du délai">
              {(
                [
                  ['Jours', false],
                  ['Mois', true],
                ] as [string, boolean][]
              ).map(([mot, mois]) => (
                <button
                  key={mot}
                  type="button"
                  className={enMois(bulle) === mois ? 'est-choisi' : ''}
                  onClick={() => {
                    const combien = (enMois(bulle) ? bulle.delaiMois : bulle.delaiJours) ?? null;
                    // On ne convertit RIEN : changer d'unité, c'est dire que le texte parlait
                    // en mois. Transformer 60 jours en 60 mois, ou en 2 mois, serait inventer.
                    onChanger(mois ? { delaiMois: combien } : { delaiJours: combien });
                  }}
                >
                  {mot}
                </button>
              ))}
            </div>
          </div>
        </LigneQuiSOuvre>
      )}

      {auPouce && (
        <button type="button" className="quand-poser" onClick={() => setDelais(true)}>
          {POSER_DATE_OU_DELAI}
        </button>
      )}
      {delais && (
        <FeuilleDesDelais bulle={bulle} onChanger={onChanger} onFermer={() => setDelais(false)} />
      )}
    </section>
  );
}

/**
 * Les repères du tuto des trois lignes, quand elles se montrent toutes (le panneau Réglages de
 * l'ordinateur) : ceux des anciens « + Échéance », « + Rendez-vous », « + Délai d'attente ».
 */
const REPERES: Record<TempsAjoute, { tuto: string }> = {
  echeance: { tuto: 'ajouter-echeance' },
  rdv: { tuto: 'ajouter-rendez-vous' },
  delai: { tuto: 'ajouter-delai' },
};

/** Ce qu'« Ajouter » pose dans « Quand ». */
export type TempsAjoute = 'echeance' | 'rdv' | 'delai';

/** L'unité en cours. Les jours par défaut : c'est ce que le monde met le plus souvent. */
function enMois(bulle: { delaiMois: number | null }): boolean {
  return bulle.delaiMois != null;
}
